import "core-js/modules/es.set.difference.v2.js";
import "core-js/modules/es.set.intersection.v2.js";
import "core-js/modules/es.set.is-disjoint-from.v2.js";
import "core-js/modules/es.set.is-subset-of.v2.js";
import "core-js/modules/es.set.is-superset-of.v2.js";
import "core-js/modules/es.set.symmetric-difference.v2.js";
import "core-js/modules/es.set.union.v2.js";
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = {
  class: "space-y-4"
};
const _hoisted_2 = {
  class: "flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
};
const _hoisted_3 = {
  class: "flex flex-wrap items-center gap-2"
};
const _hoisted_4 = {
  class: "min-w-0"
};
const _hoisted_5 = ["value"];
const _hoisted_6 = {
  class: "flex flex-wrap items-center gap-2 text-sm"
};
const _hoisted_7 = {
  class: "grid gap-4 border-t border-slate-100 pt-4 text-sm sm:grid-cols-2 xl:grid-cols-3"
};
const _hoisted_8 = {
  class: "min-w-0"
};
const _hoisted_9 = {
  class: "flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5"
};
const _hoisted_10 = {
  class: "flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-violet-50"
};
const _hoisted_11 = {
  class: "min-w-0"
};
const _hoisted_12 = {
  class: "truncate text-sm font-medium text-slate-700"
};
const _hoisted_13 = {
  class: "min-w-0"
};
import { computed } from 'vue';
import { CalendarIcon, CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, ExclamationCircleIcon, UserIcon } from '@heroicons/vue/24/outline';
import { formatReportDisplayTime, supervisorLabel } from '../workReportUtils';
import ReportStatusBadge from './ReportStatusBadge.vue';
export default {
  __name: 'ReportHeader',
  props: {
    report: {
      type: Object,
      default: () => ({})
    },
    reportDate: {
      type: String,
      required: true
    },
    activePhase: {
      type: String,
      default: 'plan'
    },
    saveStatus: {
      type: String,
      default: 'idle'
    }
  },
  emits: ['update:reportDate'],
  setup(__props, {
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const INELIGIBLE_STATUSES = new Set(['AUTO_EXEMPT', 'MANUAL_EXEMPT', 'PENDING_STATUS_CONFIRMATION']);
    const supervisor = computed(() => supervisorLabel(props.report));
    const activeDeadlineInfo = computed(() => {
      if (!props.report || INELIGIBLE_STATUSES.has(props.report.eligibilityStatus)) return null;
      const phase = props.activePhase === 'summary' ? 'summary' : 'plan';
      const meta = deadlinePhaseMeta(phase);
      const value = deadline(meta);
      if (!value || value === '--') return null;
      const missing = Boolean(props.report?.[meta.missingField]);
      const late = Boolean(props.report?.[meta.lateField]);
      const submitted = props.report?.[meta.statusField] === 'SUBMITTED';
      const submittedAt = deadlineSubmittedAt(props.report?.[meta.submittedAtField]);
      const visual = deadlineVisualState(missing ? 'missing' : late ? 'late' : submitted ? 'submitted' : 'normal');
      const prefix = missing ? '缺交' : late ? '迟交' : submitted ? '已提交' : '未提交';
      return {
        ...visual,
        icon: missing || late ? ExclamationCircleIcon : meta.icon,
        label: meta.label,
        text: `${prefix} · 截止 ${formatDeadlineValue(value)}`,
        subtext: submittedAt
      };
    });
    const saveStatusMeta = computed(() => ({
      saving: ['正在保存', 'text-blue-600', ClockIcon, 'bg-blue-50', 'text-blue-500', 'bg-blue-50'],
      saved: ['已保存', 'text-emerald-600', CheckCircleIcon, 'bg-emerald-50', 'text-emerald-500', 'bg-emerald-50/70'],
      error: ['保存失败，点击重试', 'text-rose-600', ExclamationCircleIcon, 'bg-rose-50', 'text-rose-500', 'bg-rose-50/70'],
      conflict: ['保存冲突，请重新加载', 'text-rose-600', ExclamationCircleIcon, 'bg-rose-50', 'text-rose-500', 'bg-rose-50/70'],
      dirty: ['存在未保存修改', 'text-amber-600', ExclamationCircleIcon, 'bg-amber-50', 'text-amber-500', 'bg-amber-50/70'],
      idle: ['等待编辑', 'text-slate-600', ClockIcon, 'bg-slate-100', 'text-slate-400', 'bg-slate-50']
    })[props.saveStatus] || ['等待编辑', 'text-slate-600', ClockIcon, 'bg-slate-100', 'text-slate-400', 'bg-slate-50']);
    const saveStatusLabel = computed(() => saveStatusMeta.value[0]);
    const saveStatusClass = computed(() => saveStatusMeta.value[1]);
    const saveStatusIcon = computed(() => saveStatusMeta.value[2]);
    const saveStatusIconBg = computed(() => saveStatusMeta.value[3]);
    const saveStatusIconColor = computed(() => saveStatusMeta.value[4]);
    const saveStatusBg = computed(() => saveStatusMeta.value[5]);
    function phaseDisplayStatus(phase) {
      const missingField = phase === 'plan' ? 'planMissing' : 'summaryMissing';
      const lateField = phase === 'plan' ? 'planLate' : 'summaryLate';
      if (props.report?.[missingField]) return 'MISSING';
      if (props.report?.[lateField]) return 'LATE';
      return props.report?.[`${phase}Status`] || 'DRAFT';
    }
    function deadline(meta) {
      return props.report?.[meta.deadlineField] || props.report?.deadlines?.[meta.phase] || '--';
    }
    function deadlinePhaseMeta(phase) {
      if (phase === 'summary') {
        return {
          phase: 'summary',
          label: '总结截止',
          icon: ClockIcon,
          deadlineField: 'summaryDeadline',
          statusField: 'summaryStatus',
          missingField: 'summaryMissing',
          lateField: 'summaryLate',
          submittedAtField: 'summarySubmittedAt'
        };
      }
      return {
        phase: 'plan',
        label: '计划截止',
        icon: CalendarIcon,
        deadlineField: 'planDeadline',
        statusField: 'planStatus',
        missingField: 'planMissing',
        lateField: 'planLate',
        submittedAtField: 'planSubmittedAt'
      };
    }
    function deadlineVisualState(state) {
      const map = {
        missing: ['bg-rose-50/80', 'bg-rose-100', 'text-rose-500', 'text-rose-500', 'text-rose-700', 'text-rose-500'],
        late: ['bg-amber-50/80', 'bg-amber-100', 'text-amber-500', 'text-amber-500', 'text-amber-700', 'text-amber-500'],
        submitted: ['bg-slate-50', 'bg-emerald-50', 'text-emerald-500', 'text-slate-400', 'text-slate-700', 'text-slate-400'],
        normal: ['bg-slate-50', 'bg-blue-50', 'text-blue-500', 'text-slate-400', 'text-slate-700', 'text-slate-400']
      };
      const [bgClass, iconBgClass, iconClass, labelClass, valueClass, subtextClass] = map[state] || map.normal;
      return {
        bgClass,
        iconBgClass,
        iconClass,
        labelClass,
        valueClass,
        subtextClass
      };
    }
    function deadlineSubmittedAt(value) {
      if (!value) return '';
      const formatted = formatReportDisplayTime(value);
      return formatted && formatted !== '--' ? `提交 ${formatted}` : '';
    }
    function formatDeadlineValue(value) {
      if (!value) return '--';
      const text = String(value).trim();
      if (!text) return '--';
      const dateTimeMatch = text.replace(' ', 'T').match(/T(\d{2}:\d{2})/);
      if (dateTimeMatch) return dateTimeMatch[1];
      const timeMatch = text.match(/^(\d{2}:\d{2})(?::\d{2})?$/);
      if (timeMatch) return timeMatch[1];
      return text;
    }
    function emitDateOffset(offset) {
      const value = new Date(`${props.reportDate}T00:00:00`);
      value.setDate(value.getDate() + offset);
      emit('update:reportDate', formatLocalDate(value));
    }
    function formatLocalDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_createElementVNode("div", _hoisted_3, [_createElementVNode("button", {
        type: "button",
        class: "table-action-button is-neutral",
        title: "前一天",
        "aria-label": "前一天",
        onClick: _cache[0] || (_cache[0] = $event => emitDateOffset(-1))
      }, [_createVNode(_unref(ChevronLeftIcon), {
        class: "h-4 w-4"
      })]), _createElementVNode("label", _hoisted_4, [_cache[3] || (_cache[3] = _createElementVNode("span", {
        class: "sr-only"
      }, "日报日期", -1)), _createElementVNode("input", {
        value: __props.reportDate,
        type: "date",
        class: "form-input w-full sm:w-auto",
        onChange: _cache[1] || (_cache[1] = $event => emit('update:reportDate', $event.target.value))
      }, null, 40, _hoisted_5)]), _createElementVNode("button", {
        type: "button",
        class: "table-action-button is-neutral",
        title: "后一天",
        "aria-label": "后一天",
        onClick: _cache[2] || (_cache[2] = $event => emitDateOffset(1))
      }, [_createVNode(_unref(ChevronRightIcon), {
        class: "h-4 w-4"
      })])]), _createElementVNode("div", _hoisted_6, [_cache[4] || (_cache[4] = _createElementVNode("span", {
        class: "text-slate-500"
      }, "计划", -1)), _createVNode(ReportStatusBadge, {
        status: phaseDisplayStatus('plan')
      }, null, 8, ["status"]), _cache[5] || (_cache[5] = _createElementVNode("span", {
        class: "text-slate-500"
      }, "总结", -1)), _createVNode(ReportStatusBadge, {
        status: phaseDisplayStatus('summary')
      }, null, 8, ["status"])])]), _createElementVNode("div", _hoisted_7, [activeDeadlineInfo.value ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["flex items-center gap-3 rounded-lg px-3 py-2.5", activeDeadlineInfo.value.bgClass])
      }, [_createElementVNode("div", {
        class: _normalizeClass(["flex h-8 w-8 shrink-0 items-center justify-center rounded-md", activeDeadlineInfo.value.iconBgClass])
      }, [(_openBlock(), _createBlock(_resolveDynamicComponent(activeDeadlineInfo.value.icon), {
        class: _normalizeClass(["h-4 w-4", activeDeadlineInfo.value.iconClass])
      }, null, 8, ["class"]))], 2), _createElementVNode("div", _hoisted_8, [_createElementVNode("div", {
        class: _normalizeClass(["text-xs", activeDeadlineInfo.value.labelClass])
      }, _toDisplayString(activeDeadlineInfo.value.label), 3), _createElementVNode("div", {
        class: _normalizeClass(["truncate text-sm font-medium", activeDeadlineInfo.value.valueClass])
      }, _toDisplayString(activeDeadlineInfo.value.text), 3), activeDeadlineInfo.value.subtext ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: _normalizeClass(["mt-0.5 truncate text-xs", activeDeadlineInfo.value.subtextClass])
      }, _toDisplayString(activeDeadlineInfo.value.subtext), 3)) : _createCommentVNode("", true)])], 2)) : _createCommentVNode("", true), _createElementVNode("div", _hoisted_9, [_createElementVNode("div", _hoisted_10, [_createVNode(_unref(UserIcon), {
        class: "h-4 w-4 text-violet-500"
      })]), _createElementVNode("div", _hoisted_11, [_cache[6] || (_cache[6] = _createElementVNode("div", {
        class: "text-xs text-slate-400"
      }, "直属主管", -1)), _createElementVNode("div", _hoisted_12, _toDisplayString(supervisor.value), 1)])]), _createElementVNode("div", {
        class: _normalizeClass(["flex items-center gap-3 rounded-lg px-3 py-2.5", saveStatusBg.value])
      }, [_createElementVNode("div", {
        class: _normalizeClass(["flex h-8 w-8 shrink-0 items-center justify-center rounded-md", saveStatusIconBg.value])
      }, [(_openBlock(), _createBlock(_resolveDynamicComponent(saveStatusIcon.value), {
        class: _normalizeClass(["h-4 w-4", saveStatusIconColor.value])
      }, null, 8, ["class"]))], 2), _createElementVNode("div", _hoisted_13, [_cache[7] || (_cache[7] = _createElementVNode("div", {
        class: "text-xs text-slate-400"
      }, "保存状态", -1)), _createElementVNode("div", {
        class: _normalizeClass(["truncate text-sm font-medium", saveStatusClass.value])
      }, _toDisplayString(saveStatusLabel.value), 3)])], 2)])]);
    };
  }
};